import { useState } from "react";
import { TrendingUp, TrendingDown, FlaskConical, ShieldCheck, Pencil, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { usd, pct, pnlCls } from "./polymarket/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { saveCapitalBaseline } from "@/lib/api";
import { toast } from "sonner";

const BaselineDialog = ({ open, onOpenChange, baselines }) => {
  const [vals, setVals] = useState({
    hyperliquid: baselines?.hyperliquid ?? "",
    ctrader: baselines?.ctrader ?? "",
    polymarket: baselines?.polymarket ?? "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const k of ["hyperliquid", "ctrader", "polymarket"]) {
        if (vals[k] !== "" && vals[k] != null) payload[k] = Number(vals[k]);
      }
      await saveCapitalBaseline(payload);
      toast.success("Depositi netti salvati", {
        description: "P&L ora calcolato come equity − depositi (si aggiorna entro 30s)",
      });
      onOpenChange(false);
    } catch (e) {
      toast.error("Salvataggio fallito", { description: String(e?.response?.data?.detail || e.message) });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="baseline-dialog">
        <DialogHeader>
          <DialogTitle>Depositi netti per piattaforma</DialogTitle>
          <DialogDescription>
            Quanto hai versato (meno i prelievi) su ogni piattaforma. Il P&L reale diventa
            equity − depositi: l'unica contabilità che non mente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          {[["hyperliquid", "Hyperliquid ($)"], ["ctrader", "cTrader (€)"], ["polymarket", "Polymarket ($)"]].map(([k, l]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-28 text-xs text-muted-foreground">{l}</span>
              <Input type="number" step="0.01" value={vals[k]} data-testid={`baseline-input-${k}`}
                placeholder={k === "hyperliquid" && baselines?.hyperliquidLedger != null
                  ? `auto dal ledger: ${baselines.hyperliquidLedger}$`
                  : (k === "ctrader" ? "depositi netti €" : "depositi netti $")}
                onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} />
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          {baselines?.hyperliquidLedger != null
            ? `Dal ledger on-chain Hyperliquid risultano ${baselines.hyperliquidLedger}$ di depositi netti (solo indicativo: verifica prima di usarlo). `
            : ""}Il P&L usa SOLO i valori che inserisci qui: nessun automatismo tocca la contabilità.
          Il conto cTrader è denominato in EURO: inserisci i versamenti in € (il totale in $ usa il
          cambio EURUSD dal magazzino dati). Ogni modifica viene registrata come flusso di capitale,
          così il rendimento time-weighted non conta i bonifici come profitto.
        </p>
        <Button onClick={save} disabled={busy} data-testid="baseline-save-btn" className="w-full">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Headline = REAL money under management (all platforms combined). Everything
// paper/testnet is shown small and muted, explicitly labelled as simulation.
export const PortfolioSummaryBar = ({ data }) => {
  const [editOpen, setEditOpen] = useState(false);
  const rt = data?.realTotal;
  const real = data?.real;
  const isTestnet = real?.environment === "testnet";
  const hasReal = rt ? rt.equity != null : (real && real.equity != null);
  const equity = rt ? rt.equity : real?.equity;
  const pnl = rt ? rt.pnl : real?.pnl;
  const openPos = rt ? rt.openPositions : real?.openPositions;
  const hasBaseline = rt?.baseline != null;
  const realUp = (pnl || 0) >= 0;

  return (
    <div data-testid="portfolio-summary-bar"
      className="flex flex-col gap-3 rounded-xl border border-border bg-card/50 px-3 py-2.5 md:px-4 md:py-3 backdrop-blur lg:flex-row lg:items-center">
      {/* REAL money — the headline */}
      <div className="flex items-center gap-2.5 shrink-0" data-testid="portfolio-real">
        <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg",
          isTestnet ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300")}>
          {isTestnet ? <FlaskConical className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
        </div>
        <div>
          <div className="flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-wide text-muted-foreground leading-none">
            <span>Capitale reale in gestione</span>
            {isTestnet && (
              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] md:text-[10px] font-semibold text-amber-300">TESTNET · finto</span>
            )}
            <button data-testid="baseline-edit-btn" onClick={() => setEditOpen(true)}
              className="text-muted-foreground/60 hover:text-foreground transition-colors" title="Imposta depositi netti">
              <Pencil className="h-3 w-3" />
            </button>
          </div>
          {hasReal ? (
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-xl md:text-2xl font-semibold tabular-nums leading-none" data-testid="portfolio-real-equity">
                {usd(equity)}
              </span>
              {pnl != null && (
                <span className={cn("flex items-center gap-0.5 text-xs font-medium tabular-nums", pnlCls(pnl))} data-testid="portfolio-real-pnl">
                  {realUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  <span>{usd(pnl)}</span>
                </span>
              )}
              {!hasBaseline && pnl != null && (
                <span className="text-[9px] text-amber-300/80 uppercase" title="Imposta i depositi netti per un P&L contabile esatto">stima</span>
              )}
              {openPos > 0 && (
                <span className="text-xs text-muted-foreground">{openPos} aperte</span>
              )}
            </div>
          ) : (
            <div className="mt-1.5 text-sm text-muted-foreground" data-testid="portfolio-real-none">
              Nessun conto reale collegato
            </div>
          )}
        </div>
      </div>
      <BaselineDialog open={editOpen} onOpenChange={setEditOpen} baselines={data?.baselines} />

      {/* SIMULATION — secondary, muted, for tuning the bot only */}
      <div className="lg:ml-auto flex items-center gap-2 lg:gap-3 opacity-70" data-testid="portfolio-sim">
        <div className="shrink-0">
          <div className="text-[9px] md:text-xs uppercase tracking-wide text-muted-foreground/80 leading-none">
            Simulazione · solo per ottimizzare il bot
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-sm font-medium tabular-nums leading-none text-muted-foreground" data-testid="portfolio-total-equity">
              {data ? usd(data.totalEquity) : "…"}
            </span>
            {data && (
              <span className={cn("text-xs tabular-nums", pnlCls(data.totalPnl))}>{pct(data.totalPnlPct)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto md:flex-wrap md:overflow-visible [&::-webkit-scrollbar]:hidden">
          {(data?.sleeves || []).map((s) => (
            <div key={s.key} data-testid={`portfolio-sleeve-${s.key}`}
              className="shrink-0 rounded-lg border border-border/60 bg-background/30 px-2 py-1">
              <div className="text-[9px] md:text-[10px] uppercase tracking-wide text-muted-foreground/80 leading-none">{s.label}</div>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="text-xs font-medium tabular-nums leading-none text-muted-foreground">{usd(s.equity)}</span>
                <span className={cn("text-[10px] md:text-xs tabular-nums", pnlCls(s.totalPnl))}>{pct(s.totalPnlPct)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
